import {
  getArrayOfPermissions,
  PAGE_PERMISSIONS,
} from '@mm/constants/facebook-permissions';
import { useFacebookSDKActions } from '@mm/state/actions/facebookActions';
import {
  CreatePayload,
  useFacebookSessionCRUDActions,
} from '@mm/state/actions/FacebookSessionCRUDActions';
import JSONUtils from '@mm/state/helpers/json-utils';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectInviteToken } from '@mm/state/selectors/userSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { LocalStorageService } from '@mm/utils';
import React, { useState } from 'react';
import { ReactCookieProps, withCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import Button from '../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import Input from '../../../../../common/components/Input/Input';
import { EMAIL_REGEX } from '../../../../../common/constants/validations';
import { PREV_LOCATION } from '../../../../../common/helpers/routes/GuardedPrivateRoute';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProcessingBlinder,
} from '../../../../../common/modals/modal-helpers';
import { setManualFacebookEmail } from '@mm/state/modules/registerFlowState/actions';
import './AddEmailModal.pcss';

const HEADER = "What's your email address?";

const MESSAGE = `
  We're having trouble getting your email address from Facebook.
  Could you please enter it below?`;

const AddEmailModal = (
  props: CreatePayload & ReactCookieProps & { closeModal: () => void },
) => {
  const { cookies, userAttributes, closeModal } = props;

  const inviteToken = useSelector(selectInviteToken);

  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState('');

  const facebookSDKActions = useFacebookSDKActions();
  const facebookSessionCRUDActions = useFacebookSessionCRUDActions();

  const stringifiedPrevLocation = LocalStorageService.getItem(PREV_LOCATION);
  const prevLocation = JSONUtils.safeParse<Location>(stringifiedPrevLocation);
  const parsedPreviousPathname = prevLocation?.pathname?.split('/');
  const isCustomPlan =
    parsedPreviousPathname?.[1] === 'subscribe' &&
    !!parsedPreviousPathname?.[2];

  const invalidEmail = !EMAIL_REGEX.test(email);

  const dispatch: Dispatch<StoreState> = useDispatch();

  const setManualFbEmail = (email: string) =>
    // @ts-ignore
    dispatch(setManualFacebookEmail(email));

  const handleEmailChange = event => {
    setEmail(event.target.value);
  };

  const submitEmail = async event => {
    event.preventDefault();

    const invalidEmail = !EMAIL_REGEX.test(email);

    if (invalidEmail) {
      return;
    }

    const extraParams = {
      userAttributes: {
        ...userAttributes,
        freeTrial: !isCustomPlan,
      },
    };

    try {
      setIsProcessing(true);
      const permissionsToRequest = getArrayOfPermissions(PAGE_PERMISSIONS);
      const permissionsResponse =
        await facebookSDKActions.requestPermissionsWithManualFlowFallback(
          permissionsToRequest,
          {
            email,
          },
        );
      if (!permissionsResponse || !permissionsResponse.facebookToken) {
        return closeModal();
      }
      const { facebookToken } = permissionsResponse;
      facebookSessionCRUDActions.create({
        facebookToken,
        email,
        inviteToken,
        ...extraParams,
      });
      setManualFbEmail(email);
      closeModal();
    } catch (error) {
      closeModal();
      return error;
    }
  };

  return (
    <form className="add-email-modal" onSubmit={submitEmail}>
      <ModalHeader>
        <h2>{HEADER}</h2>
      </ModalHeader>
      <ModalBody>
        <p className="add-email-modal__message">{MESSAGE}</p>

        <Input type="text" value={email} onChange={handleEmailChange} />
      </ModalBody>
      <ModalFooter>
        <Button disabled={invalidEmail} onClick={submitEmail}>
          Ok
        </Button>
      </ModalFooter>

      <ModalProcessingBlinder show={isProcessing} />
    </form>
  );
};

const enhance = compose(withCookies);

export default enhance(AddEmailModal);
